import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Activity, Cable, CheckCircle2, ChevronDown, ChevronUp, CircuitBoard, Edit, Loader2, Plus, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';
import { ioApi, type IoModule, type IoTestResult } from '../../utils/ioModules';
import { BridgePicker, bridgeName, useClientBridges } from './BridgePicker';
import { BulkBridgeAssign } from './BulkBridgeAssign';

const selectCls = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const BAUD_RATES = [2400, 4800, 9600, 19200, 38400, 57600, 115200];
const COM_PORTS = Array.from({ length: 20 }, (_, i) => `COM${i + 1}`);
const CH = Array.from({ length: 8 }, (_, i) => i);
const DI_COLOR = '#10b981';
const DO_COLOR = '#06b6d4';

type Form = {
  name: string; location: string; description: string;
  bridgeId: string;
  serialPort: string; baudRate: string; parity: 'none' | 'even' | 'odd'; dataBits: string; stopBits: string; slaveId: string;
  pollIntervalSec: string; timeoutMs: string; isActive: boolean;
  inputs: string[]; outputs: string[];
  inputVisible: boolean[]; outputVisible: boolean[]; panel: string;
};

const emptyForm = (): Form => ({
  name: '', location: '', description: '', bridgeId: '',
  serialPort: 'COM1', baudRate: '9600', parity: 'none', dataBits: '8', stopBits: '1', slaveId: '1',
  pollIntervalSec: '0.3', timeoutMs: '1000', isActive: true,
  inputs: CH.map((i) => `DI${i + 1}`), outputs: CH.map((i) => `DO${i + 1}`),
  inputVisible: CH.map(() => true), outputVisible: CH.map(() => true), panel: 'Main panel',
});

function Lamp({ on, color, label }: { on: boolean | null; color: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <motion.span
        className="h-4 w-4 rounded-full"
        animate={{ backgroundColor: on ? color : 'rgba(127,127,127,0.25)', boxShadow: on ? `0 0 10px ${color}` : '0 0 0 transparent' }}
      />
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
  );
}

export function IoModulesTab() {
  const token = sessionStorage.get()?.token || '';
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState('');
  const bridges = useClientBridges(token, selectedClient);
  const [list, setList] = useState<IoModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<IoModule | null>(null);
  const [form, setForm] = useState<Form>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<IoModule | null>(null);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<IoTestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [savingDrawing, setSavingDrawing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cl = await adminApi.getClients(token);
        setClients(cl);
        if (cl.length) setSelectedClient(cl[0].id);
      } catch (e: any) {
        toast.error(e.message || 'Failed to load clients');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const load = async (clientId = selectedClient) => {
    if (!clientId) return;
    setLoading(true);
    try { setList(await ioApi.adminList(token, clientId)); } catch (e: any) { toast.error(e.message); setList([]); } finally { setLoading(false); }
  };
  useEffect(() => { if (selectedClient) load(selectedClient); }, [selectedClient]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  // Existing panel names for this client, offered as suggestions.
  const panelNames = Array.from(new Set(list.map((m) => m.labels?.panel || 'Main panel')));

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm(), panel: panelNames[0] || 'Main panel' }); setTest(null); setTestError(null); setOpen(true); };
  const openEdit = (m: IoModule) => {
    setEditing(m);
    setForm({
      name: m.name, location: m.location || '', description: m.description || '', bridgeId: m.bridgeId || '',
      serialPort: m.serialPort || 'COM1', baudRate: String(m.baudRate || 9600), parity: (m.parity || 'none') as Form['parity'],
      dataBits: String(m.dataBits || 8), stopBits: String(m.stopBits || 1), slaveId: String(m.slaveId || 1),
      pollIntervalSec: String((m.pollIntervalMs || 1000) / 1000), timeoutMs: String(m.timeoutMs || 1000), isActive: m.isActive !== false,
      inputs: CH.map((i) => m.labels?.inputs?.[i] || `DI${i + 1}`), outputs: CH.map((i) => m.labels?.outputs?.[i] || `DO${i + 1}`),
      inputVisible: CH.map((i) => m.labels?.inputVisible?.[i] !== false), outputVisible: CH.map((i) => m.labels?.outputVisible?.[i] !== false),
      panel: m.labels?.panel || 'Main panel',
    });
    setTest(null); setTestError(null); setOpen(true);
  };

  const connection = () => ({
    bridgeId: form.bridgeId || null,
    serialPort: form.serialPort.trim().toUpperCase(), baudRate: Number(form.baudRate), parity: form.parity,
    dataBits: Number(form.dataBits), stopBits: Number(form.stopBits), slaveId: Number(form.slaveId), timeoutMs: Number(form.timeoutMs || 1000),
  });

  const runTest = async () => {
    if (!form.serialPort.trim()) { toast.error('Enter the COM port first'); return; }
    setTesting(true); setTest(null); setTestError(null);
    try {
      const r = await ioApi.test(token, selectedClient, { ...connection(), id: editing?.id, name: form.name || 'IO module', modelId: 'waveshare-8ch' });
      setTest(r);
      if (r.ok) toast.success('Module answered', { description: r.link }); else toast.error('Test failed', { description: r.error || 'No reply' });
    } catch (e: any) {
      setTestError(e.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Module name is required'); return; }
    if (!form.serialPort.trim()) { toast.error('COM port is required'); return; }
    const payload: Record<string, any> = {
      name: form.name.trim(), location: form.location.trim(), description: form.description.trim(), modelId: 'waveshare-8ch',
      ...connection(), pollIntervalMs: Math.round(Number(form.pollIntervalSec || 0.3) * 1000), isActive: form.isActive,
      labels: { inputs: form.inputs, outputs: form.outputs, inputVisible: form.inputVisible, outputVisible: form.outputVisible, panel: form.panel.trim() || 'Main panel' },
    };
    setSaving(true);
    try {
      if (editing) { await ioApi.update(token, selectedClient, editing.id, payload); toast.success('IO module updated', { description: 'The bridge picks up changes within about a minute.' }); }
      else { await ioApi.create(token, selectedClient, payload); toast.success('IO module added', { description: 'The bridge starts reading it within about a minute.' }); }
      setOpen(false); load();
    } catch (e: any) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // The animated module drawing on the client's screen, on unless switched off for that client.
  const drawingOn = clients.find((c) => c.id === selectedClient)?.modules?.ioPanelDrawing !== false;
  const toggleDrawing = async (show: boolean) => {
    const clientId = selectedClient;
    setSavingDrawing(true);
    try {
      await ioApi.setPanelDrawing(token, clientId, show);
      setClients((prev) => prev.map((c) => (c.id === clientId ? { ...c, modules: { ...c.modules, ioPanelDrawing: show } } : c)));
      toast.success(show ? 'Panel drawing switched on' : 'Panel drawing switched off', { description: 'The client screen updates within about 30 seconds.' });
    } catch (e: any) {
      toast.error(e.message || 'Failed to save');
    } finally {
      setSavingDrawing(false);
    }
  };

  const toggleActive = async (m: IoModule) => {
    try { await ioApi.update(token, selectedClient, m.id, { isActive: !(m.isActive !== false) }); load(); } catch (e: any) { toast.error(e.message); }
  };
  const confirmDelete = async () => {
    if (!deleting) return;
    try { await ioApi.remove(token, selectedClient, deleting.id); toast.success(`Deleted ${deleting.name}`); setDeleting(null); load(); } catch (e: any) { toast.error(e.message); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-2xl font-bold"><CircuitBoard className="h-6 w-6" /> IO Modules (Waveshare)</h2>
          <p className="text-sm text-muted-foreground">
            Waveshare Modbus RTU IO 8CH — 8 digital inputs and 8 relay outputs over RS-485. Clients see and switch them on the Waveshare 8CH screen.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select className={`${selectCls} sm:w-[220px]`} value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)} aria-label="Client">
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <BulkBridgeAssign token={token} clientId={selectedClient} onDone={() => load()} />
          <Button onClick={openCreate} disabled={!selectedClient}><Plus className="mr-2 h-4 w-4" /> Add IO module</Button>
        </div>
      </div>

      {selectedClient && (
        <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
          <div className="min-w-0">
            <Label htmlFor="io-panel-drawing" className="font-semibold">Animated panel drawing</Label>
            <p className="mt-1 text-xs text-muted-foreground">Show the module drawing with LEDs on this client's Waveshare screen. When off, the client still sees the counts and the Digital inputs list.</p>
          </div>
          <Switch id="io-panel-drawing" checked={drawingOn} disabled={savingDrawing} onCheckedChange={toggleDrawing} />
        </div>
      )}

      {loading ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>
      ) : list.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <CircuitBoard className="h-12 w-12 text-muted-foreground opacity-50" />
            <div className="text-lg font-semibold">No IO modules for this client</div>
            <p className="max-w-md text-sm text-muted-foreground">Add the Waveshare module with its COM port, baud rate and slave ID (factory default 9600 8N1, address 1), then use Test connection.</p>
            <Button onClick={openCreate} disabled={!selectedClient}><Plus className="mr-2 h-4 w-4" /> Add the first IO module</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {list.map((m) => (
            <Card key={m.id} className={m.isActive === false ? 'opacity-60' : ''}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="truncate text-lg">{m.name}</CardTitle>
                    <CardDescription className="truncate">{m.modelName}{m.location ? ` — ${m.location}` : ''}</CardDescription>
                  </div>
                  <Switch checked={m.isActive !== false} onCheckedChange={() => toggleActive(m)} aria-label="Active" />
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Cable className="h-4 w-4" />
                  <span className="truncate">Modbus RTU · {m.serialPort} · {m.baudRate} {m.dataBits}{(m.parity || 'none')[0].toUpperCase()}{m.stopBits} · slave {m.slaveId}</span>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="rounded-md bg-muted px-2 py-1">Panel: {m.labels?.panel || 'Main panel'}</span>
                  <span className="rounded-md bg-muted px-2 py-1">{bridgeName(bridges, m.bridgeId) || 'Any bridge PC'}</span>
                  <span className="rounded-md bg-muted px-2 py-1">{(m.labels?.inputVisible || []).filter((v) => v !== false).length}/{m.inputs} inputs shown · read-only</span>
                  <span className="rounded-md bg-muted px-2 py-1">every {((m.pollIntervalMs || 1000) / 1000).toFixed(1)} s</span>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(m)}><Edit className="mr-1.5 h-4 w-4" /> Edit / test</Button>
                  <Button variant="outline" size="sm" onClick={() => setDeleting(m)} aria-label={`Delete ${m.name}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => !saving && !testing && setOpen(o)}>
        <DialogContent className="max-h-[92vh] w-[96vw] overflow-y-auto sm:max-w-[1000px] [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add IO module'}</DialogTitle>
            <DialogDescription>Waveshare Modbus RTU IO 8CH. Inputs are read-only; outputs are switched from the client's Waveshare 8CH screen.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-1.5"><Label>Module name *</Label><Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Pump room IO" /></div>
              <div className="space-y-1.5"><Label>Location</Label><Input value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. Panel 2" /></div>
              <div className="space-y-1.5"><Label>Description</Label><Input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Optional" /></div>
            </div>

            <BridgePicker bridges={bridges} value={form.bridgeId} onChange={(v) => set('bridgeId', v)} id="io-bridge-pc" />

            <div className="rounded-lg border p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold"><Cable className="h-4 w-4" /> Modbus RTU (RS-485)</div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>COM port *</Label>
                  <Input list="io-com-ports" value={form.serialPort} onChange={(e) => set('serialPort', e.target.value)} placeholder="COM3" />
                  <datalist id="io-com-ports">{COM_PORTS.map((p) => <option key={p} value={p} />)}</datalist>
                </div>
                <div className="space-y-1.5"><Label>Baud rate</Label><select className={selectCls} value={form.baudRate} onChange={(e) => set('baudRate', e.target.value)}>{BAUD_RATES.map((b) => <option key={b} value={b}>{b}</option>)}</select></div>
                <div className="space-y-1.5"><Label>Slave ID (device address)</Label><Input type="number" min={1} max={247} value={form.slaveId} onChange={(e) => set('slaveId', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Parity</Label><select className={selectCls} value={form.parity} onChange={(e) => set('parity', e.target.value as Form['parity'])}><option value="none">None</option><option value="even">Even</option><option value="odd">Odd</option></select></div>
                <div className="space-y-1.5"><Label>Data bits</Label><select className={selectCls} value={form.dataBits} onChange={(e) => set('dataBits', e.target.value)}><option value="8">8</option><option value="7">7</option></select></div>
                <div className="space-y-1.5"><Label>Stop bits</Label><select className={selectCls} value={form.stopBits} onChange={(e) => set('stopBits', e.target.value)}><option value="1">1</option><option value="2">2</option></select></div>
                <div className="space-y-1.5"><Label>Update every (seconds)</Label><Input type="number" min={0.2} max={60} step={0.1} value={form.pollIntervalSec} onChange={(e) => set('pollIntervalSec', e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Reply timeout (ms)</Label><Input type="number" min={200} max={10000} step={100} value={form.timeoutMs} onChange={(e) => set('timeoutMs', e.target.value)} /></div>
                <div className="flex items-end gap-3 pb-1"><Switch id="io-active" checked={form.isActive} onCheckedChange={(v) => set('isActive', v)} /><Label htmlFor="io-active">Active (polled by bridge)</Label></div>
              </div>
            </div>

            <div className="rounded-lg border">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
                <div className="text-sm font-semibold">Test connection</div>
                <Button type="button" size="sm" onClick={runTest} disabled={testing}>
                  {testing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Activity className="mr-1.5 h-3.5 w-3.5" />} Test now
                </Button>
              </div>
              <div className="p-3 text-sm">
                {!test && !testError && !testing && <p className="text-muted-foreground">The bridge at the site reads all 8 inputs and 8 outputs once with the settings above. Nothing is switched.</p>}
                {testing && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Asking the site bridge to read the module… (up to 45 s)</div>}
                {testError && <div className="flex items-start gap-2 text-red-600 dark:text-red-400"><XCircle className="mt-0.5 h-4 w-4 shrink-0" /> {testError}</div>}
                {test && !testing && (
                  <div className="space-y-3">
                    <div className={`flex items-start gap-2 rounded-md border p-2.5 ${test.ok ? 'border-emerald-500/40 bg-emerald-500/10' : 'border-red-500/40 bg-red-500/10'}`}>
                      {test.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />}
                      <div>
                        <div className="font-semibold">{test.ok ? 'Module is answering' : 'No valid reply'}</div>
                        <div className="text-xs text-muted-foreground">{test.link}{test.elapsedMs != null ? ` · ${test.elapsedMs} ms on the bridge` : ''}</div>
                        {test.error && <div className="mt-1 text-xs text-red-600 dark:text-red-400">{test.error}</div>}
                      </div>
                    </div>
                    {test.ok && (
                      <div className="grid gap-3">
                        <div className="rounded-md border p-3">
                          <div className="mb-2 text-xs font-semibold">Inputs (FC02){test.inputsError ? ` — ${test.inputsError}` : ''}</div>
                          <div className="flex justify-between">{CH.map((i) => <Lamp key={i} on={test.inputs ? !!test.inputs[i] : null} color={DI_COLOR} label={`DI${i + 1}`} />)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="mb-1 text-sm font-semibold">Client panel</div>
              <p className="mb-3 text-xs text-muted-foreground">
                Modules with the same panel name are merged into one panel on the client's Waveshare screen. Give every module the same name for a single combined panel.
              </p>
              <div className="max-w-sm space-y-1.5">
                <Label>Panel</Label>
                <Input list="io-panels" value={form.panel} maxLength={60} onChange={(e) => set('panel', e.target.value)} placeholder="Main panel" />
                <datalist id="io-panels">{panelNames.map((p) => <option key={p} value={p} />)}</datalist>
              </div>
            </div>

            <div className="rounded-lg border p-4">
              <div className="mb-1 text-sm font-semibold">Inputs</div>
              <p className="mb-3 text-xs text-muted-foreground">
                Name each input (e.g. “Tank high level”). Use the arrows to move a name to the input above or below. Switch off <b>Show</b> to hide an input from the client. Monitoring is read-only: the bridge reads inputs only and never writes to the module.
              </p>
              <div className="grid gap-5 md:grid-cols-2">
                {(['inputs'] as const).map((kind) => {
                  const visKey = kind === 'inputs' ? 'inputVisible' : 'outputVisible';
                  const shown = form[visKey].filter(Boolean).length;
                  // Move a name (and its Show setting) to the neighbouring channel; the DI numbers stay put.
                  const move = (i: number, d: number) => setForm((f) => {
                    const j = i + d;
                    if (j < 0 || j >= CH.length) return f;
                    const swap = <T,>(a: T[]) => a.map((v, k) => (k === i ? a[j] : k === j ? a[i] : v));
                    return { ...f, [kind]: swap(f[kind]), [visKey]: swap(f[visKey]) };
                  });
                  return (
                    <div key={kind} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold" style={{ color: kind === 'inputs' ? DI_COLOR : DO_COLOR }}>
                          {kind === 'inputs' ? 'Digital inputs' : 'Relay outputs'} <span className="font-normal text-muted-foreground">· {shown}/8 shown</span>
                        </div>
                        <div className="flex gap-1">
                          <button type="button" className="rounded border px-2 py-0.5 text-[11px] hover:bg-muted" onClick={() => set(visKey, CH.map(() => true))}>Show all</button>
                          <button type="button" className="rounded border px-2 py-0.5 text-[11px] hover:bg-muted" onClick={() => set(visKey, CH.map(() => false))}>Hide all</button>
                        </div>
                      </div>
                      {CH.map((i) => (
                        <div key={i} className={`flex items-center gap-2 ${form[visKey][i] ? '' : 'opacity-50'}`}>
                          <span className="w-10 text-xs font-mono text-muted-foreground">{kind === 'inputs' ? 'DI' : 'DO'}{i + 1}</span>
                          <div className="flex shrink-0 flex-col">
                            <button type="button" className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
                              disabled={i === 0} onClick={() => move(i, -1)} title={`Move this name to ${kind === 'inputs' ? 'DI' : 'DO'}${i}`} aria-label="Move name up">
                              <ChevronUp className="h-3.5 w-3.5" />
                            </button>
                            <button type="button" className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
                              disabled={i === CH.length - 1} onClick={() => move(i, 1)} title={`Move this name to ${kind === 'inputs' ? 'DI' : 'DO'}${i + 2}`} aria-label="Move name down">
                              <ChevronDown className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <Input className="h-8 text-sm" value={form[kind][i]} maxLength={40}
                            onChange={(e) => setForm((f) => ({ ...f, [kind]: f[kind].map((v, j) => (j === i ? e.target.value : v)) }))} />
                          <label className="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground" title="Show to client">
                            <Switch checked={form[visKey][i]} onCheckedChange={(v) => setForm((f) => ({ ...f, [visKey]: f[visKey].map((x, j) => (j === i ? v : x)) }))} aria-label={`Show ${kind === 'inputs' ? 'DI' : 'DO'}${i + 1} to client`} />
                            Show
                          </label>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              <div className="mb-2 font-medium text-foreground">Modbus address map (fixed for this module)</div>
              <table className="w-full text-xs">
                <thead><tr className="text-left"><th className="py-1">Signal</th><th className="py-1">Function</th><th className="py-1">Address</th></tr></thead>
                <tbody>
                  <tr className="border-t"><td className="py-1">DI1 – DI8 (inputs)</td><td>FC02 read discrete inputs</td><td className="font-mono">0 – 7 (10001 – 10008)</td></tr>
                </tbody>
              </table>
              <p className="mt-2 text-xs">Factory default: 9600 baud, 8N1, device address 1. The module can share an RS-485 port with energy meters/VFDs/controllers if the serial settings match and each device has its own address.</p>
            </div>
          </div>

          <div className="flex justify-between gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving || testing}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{editing ? 'Save changes' : 'Add IO module'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {deleting?.name}?</DialogTitle>
            <DialogDescription>The bridge stops reading this module and it disappears from the client's Waveshare 8CH screen.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}><Trash2 className="mr-2 h-4 w-4" /> Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
